import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid2,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import IconButton from "../../components/ui/IconButton";
import SearchField from "../../components/ui/SearchField";
import InfoIcon from "@mui/icons-material/Info";
import { toast } from "react-toastify";
import api from "../../services/api";
import { useIntl } from "../../contexts/IntlContext";
import DatePickerField from "../../components/Dashboard/DatePickerField";
import { SubscriptionDetailDialog } from "./SubscriptionDetailDialog";
import { TableEmptyState } from "../../components/table/TableEmptyState";
import { TableSkeletonRows } from "../../components/table/TableSkeletonRows";
import { StandardTablePagination } from "../../components/table/StandardTablePagination";
import { PageHeader } from "../../components/PageHeader";
import Select from "../../components/ui/Select";
import FiltersBar from "../../components/ui/FiltersBar";
import { fontWeights } from "../../themes/fontWeights";
import StatusPill, { StatusPillLevel } from "../../components/ui/StatusPill";
import { getSubscriptionStatusLabel, getSubscriptionStatusLevel } from "./subscriptionStatus";
import { SubscriptionRow } from "./types";

type DashboardMetrics = {
  activeSubscriptions: number;
  cancelledSubscriptions: number;
  expiredSubscriptions: number;
  singlePurchases: number;
  subscriptionRevenueCollected: number;
  subscriptionRevenueTrial: number;
  singleRevenue: number;
  totalRevenue: number;
};

type Filters = {
  search: string;
  type: string;
  status: string;
  offering: string;
  startDate: string;
  endDate: string;
  sortBy: string;
  sortOrder: string;
  includeTest: boolean;
  billingPhase: string;
};

type SortField = "createdAt" | "amount" | "status";

const initialFilters: Filters = {
  search: "",
  type: "",
  status: "approved",
  offering: "",
  startDate: "",
  endDate: "",
  sortBy: "createdAt",
  sortOrder: "desc",
  includeTest: false,
  billingPhase: "",
};

type Column = {
  id: string;
  label: string;
  minWidth: number;
  hideOnMobile?: boolean;
};

const toFiniteNumber = (value: unknown): number | null => {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeDashboardMetrics = (payload: unknown): DashboardMetrics | null => {
  const data = payload as Partial<Record<keyof DashboardMetrics, unknown>> | null | undefined;
  const activeSubscriptions = toFiniteNumber(data?.activeSubscriptions);
  const cancelledSubscriptions = toFiniteNumber(data?.cancelledSubscriptions);
  const expiredSubscriptions = toFiniteNumber(data?.expiredSubscriptions);
  const singlePurchases = toFiniteNumber(data?.singlePurchases);
  const subscriptionRevenueCollected = toFiniteNumber(data?.subscriptionRevenueCollected);
  const subscriptionRevenueTrial = toFiniteNumber(data?.subscriptionRevenueTrial);
  const singleRevenue = toFiniteNumber(data?.singleRevenue);
  const totalRevenue = toFiniteNumber(data?.totalRevenue);

  if (
    activeSubscriptions == null ||
    cancelledSubscriptions == null ||
    expiredSubscriptions == null ||
    singlePurchases == null ||
    subscriptionRevenueCollected == null ||
    subscriptionRevenueTrial == null ||
    singleRevenue == null ||
    totalRevenue == null
  ) {
    return null;
  }

  return {
    activeSubscriptions,
    cancelledSubscriptions,
    expiredSubscriptions,
    singlePurchases,
    subscriptionRevenueCollected,
    subscriptionRevenueTrial,
    singleRevenue,
    totalRevenue,
  };
};

const normalizeSubscriptionListResponse = (
  payload: unknown,
): { rows: SubscriptionRow[]; count: number; hiddenTestCount: number } | null => {
  const data = payload as { data?: unknown; count?: unknown; hiddenTestCount?: unknown } | null | undefined;
  if (!data || !Array.isArray(data.data)) return null;
  const rows = data.data as SubscriptionRow[];

  const count = toFiniteNumber(data?.count);
  if (count == null) return null;

  return {
    rows,
    count,
    hiddenTestCount: toFiniteNumber(data?.hiddenTestCount) ?? 0,
  };
};

const getErrorMessage = (intl: ReturnType<typeof useIntl>, error: any, fallbackId: string) => {
  const responseMessage =
    typeof error?.response?.data?.message === "string" ? error.response.data.message.trim() : "";
  if (responseMessage) return responseMessage;
  if (typeof error?.message === "string" && error.message.trim()) return error.message.trim();
  return intl.formatMessage({ id: fallbackId });
};

function formatCurrency(value: number): string {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const STATUS_PILL_TO_CHIP_COLOR: Record<StatusPillLevel, "success" | "warning" | "error" | "default"> = {
  success: "success",
  warning: "warning",
  error: "error",
  neutral: "default",
};

// Removable filter chip — StatusPill has no onDelete, so this stays a Chip,
// but reuses the same status->level/label source as the read-only StatusPill uses.
const statusFilterChip = (intl: ReturnType<typeof useIntl>, status: string, onDelete?: () => void) => (
  <Chip
    size="small"
    label={getSubscriptionStatusLabel(intl, status)}
    color={STATUS_PILL_TO_CHIP_COLOR[getSubscriptionStatusLevel(status)]}
    onDelete={onDelete}
  />
);

const typeLabel = (intl: ReturnType<typeof useIntl>, type: string): string => {
  if (type === "subscription") return intl.formatMessage({ id: "subscriptionDetail.type.subscription" });
  if (type === "single") return intl.formatMessage({ id: "subscriptionDetail.type.singlePurchase" });
  return type;
};

function offeringLabel(offering: string): string {
  if (offering === "premium") return "Premium";
  if (offering === "goalkeeper") return "Goleiro";
  return offering;
}

export function SubscriptionsDashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const intl = useIntl();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [rows, setRows] = useState<SubscriptionRow[]>([]);
  const [count, setCount] = useState(0);
  const [hiddenTestCount, setHiddenTestCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const columns: Column[] = useMemo(() => [
    { id: "visitorName", label: intl.formatMessage({ id: "subscriptions.columnName" }), minWidth: 140 },
    { id: "offering", label: intl.formatMessage({ id: "subscriptions.columnOffering" }), minWidth: 130 },
    { id: "amount", label: intl.formatMessage({ id: "subscriptions.columnAmount" }), minWidth: 90 },
    { id: "status", label: intl.formatMessage({ id: "subscriptions.columnStatus" }), minWidth: 110 },
    { id: "createdAt", label: intl.formatMessage({ id: "subscriptions.columnDate" }), minWidth: 110 },
    { id: "actions", label: "", minWidth: 40 },
  ], [intl]);

  const visibleColumns = useMemo(
    () => columns.filter((col) => !isMobile || !col.hideOnMobile),
    [columns, isMobile],
  );

  const fetchMetrics = useCallback(async () => {
    setMetricsLoading(true);
    setMetricsError(null);
    try {
      const response = await api.get("/app/admin/subscriptions/dashboard");
      const normalized = normalizeDashboardMetrics(response.data);
      if (!normalized) {
        throw new Error(intl.formatMessage({ id: "dashboard.loadError" }));
      }
      setMetrics(normalized);
    } catch (error: any) {
      setMetrics(null);
      setMetricsError(getErrorMessage(intl, error, "dashboard.loadError"));
    } finally {
      setMetricsLoading(false);
    }
  }, [intl]);

  const fetchList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const params: Record<string, string | number> = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (filters.type) params.type = filters.type;
      if (filters.status) params.status = filters.status;
      if (filters.offering) params.offering = filters.offering;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.includeTest) params.includeTest = "true";
      if (filters.billingPhase) params.billingPhase = filters.billingPhase;

      const response = await api.get("/app/admin/subscriptions/list", { params });
      const normalized = normalizeSubscriptionListResponse(response.data);
      if (!normalized) {
        throw new Error(intl.formatMessage({ id: "dashboard.loadError" }));
      }
      setRows(normalized.rows);
      setCount(normalized.count);
      setHiddenTestCount(normalized.hiddenTestCount);
    } catch (error: any) {
      const message =
        error?.response?.status === 400 || error?.response?.status === 401
          ? getErrorMessage(intl, error, "dashboard.loadError")
          : getErrorMessage(intl, error, "messages.connectionError");
      setRows([]);
      setCount(0);
      setHiddenTestCount(0);
      setListError(message);
      toast.error(message);
    } finally {
      setListLoading(false);
    }
  }, [
    page,
    rowsPerPage,
    debouncedSearch,
    filters.type,
    filters.status,
    filters.offering,
    filters.startDate,
    filters.endDate,
    filters.sortBy,
    filters.sortOrder,
    filters.includeTest,
    filters.billingPhase,
    intl,
  ]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters.search]);

  const handleFilterChange = (field: keyof Filters, value: string | boolean) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    if (field !== 'sortBy' && field !== 'sortOrder' && field !== 'search') {
      setPage(0);
    }
  };

  const handleSortChange = (field: SortField) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === "asc" ? "desc" : "asc",
    }));
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleOpenDetail = (transactionId: string) => {
    setSelectedTransactionId(transactionId);
    setDetailOpen(true);
  };

  const MetricCard = ({ title, value, color }: { title: string; value: string; color?: string }) => (
    <Card sx={{ height: "100%" }}>
      <CardContent sx={{ p: isMobile ? 1.5 : 2, "&:last-child": { pb: isMobile ? 1.5 : 2 } }}>
        <Typography variant={isMobile ? "caption" : "body2"} color="textSecondary" gutterBottom sx={{ fontWeight: fontWeights.medium }}>
          {title}
        </Typography>
        <Typography
          variant={isMobile ? "h6" : "h5"}
          fontWeight="bold"
          sx={{ color: color || "text.primary", lineHeight: 1.2 }}
        >
          {value}
        </Typography>
      </CardContent>
    </Card>
  );

  const activeFilterCount = [
    filters.search,
    filters.type,
    filters.status,
    filters.offering,
    filters.startDate,
    filters.endDate,
  ].filter(Boolean).length;

  const sortIndicator = (field: SortField) => {
    if (filters.sortBy !== field) return "";
    return filters.sortOrder === "asc" ? " ↑" : " ↓";
  };

  const loadingLabel = intl.formatMessage({ id: "common.loading" });
  const metricCountValue = (value: number | undefined) => {
    if (metricsLoading) return loadingLabel;
    if (metricsError || typeof value !== "number") return "-";
    return String(value);
  };
  const metricCurrencyValue = (value: number | undefined) => {
    if (metricsLoading) return loadingLabel;
    if (metricsError || typeof value !== "number") return "-";
    return formatCurrency(value);
  };

  const renderCell = (column: Column, row: SubscriptionRow) => {
    switch (column.id) {
      case "visitorName":
        return row.visitorName || "-";
      case "visitorEmail":
        return row.visitorEmail || "-";
      case "type":
        return typeLabel(intl, row.type);
      case "offering":
        return `${typeLabel(intl, row.type)} · ${offeringLabel(row.offering)}`;
      case "amount":
        return formatCurrency(row.amount);
      case "status":
        return (
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <StatusPill size="small" level={getSubscriptionStatusLevel(row.status)} label={getSubscriptionStatusLabel(intl, row.status)} />
            {row.isInTrial && (
              <Chip size="small" variant="outlined" color="info" label={intl.formatMessage({ id: "subscriptions.trialChip" })} />
            )}
            {row.isTest && (
              <Chip size="small" variant="outlined" color="warning" label={intl.formatMessage({ id: "subscriptions.testChip" })} />
            )}
          </Stack>
        );
      case "createdAt":
        return formatDateTime(row.createdAt);
      case "nextBillingDate":
        return formatDate(row.nextBillingDate);
      case "cancellationCompletedAt":
        return formatDate(row.cancellationCompletedAt);
      case "gatewayPaymentId":
        return row.gatewayPaymentId ? (
          <Tooltip title={row.gatewayPaymentId}>
            <Typography variant="body2" noWrap sx={{ maxWidth: 100 }}>
              {row.gatewayPaymentId}
            </Typography>
          </Tooltip>
        ) : (
          "-"
        );
      case "actions":
        return (
          <IconButton onClick={() => handleOpenDetail(row.transactionId)}>
            <InfoIcon fontSize="small" />
          </IconButton>
        );
      default:
        return String((row as any)[column.id] ?? "-");
    }
  };

  return (
    <>
      {/* Dashboard Metrics */}
      {metricsError ? (
        <Alert severity="error" sx={{ mb: isMobile ? 1.5 : 2 }}>
          {metricsError}
        </Alert>
      ) : null}
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        {intl.formatMessage({ id: "subscriptions.metricsExcludeTestNote" })}
      </Typography>

      <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        {intl.formatMessage({ id: "subscriptions.metricsCounts" })}
      </Typography>
      <Grid2 container spacing={isMobile ? 1 : 2} sx={{ mb: isMobile ? 1.5 : 2.5 }}>
        <Grid2 size={{ xs: 6, sm: 6, md: 3 }}>
          <MetricCard title={intl.formatMessage({ id: "subscriptions.activeSubscriptions" })} value={metricCountValue(metrics?.activeSubscriptions)} color="success.main" />
        </Grid2>
        <Grid2 size={{ xs: 6, sm: 6, md: 3 }}>
          <MetricCard title={intl.formatMessage({ id: "subscriptions.cancelledSubscriptions" })} value={metricCountValue(metrics?.cancelledSubscriptions)} color="error.main" />
        </Grid2>
        <Grid2 size={{ xs: 6, sm: 6, md: 3 }}>
          <MetricCard title={intl.formatMessage({ id: "subscriptions.expiredSubscriptions" })} value={metricCountValue(metrics?.expiredSubscriptions)} color="warning.main" />
        </Grid2>
        <Grid2 size={{ xs: 6, sm: 6, md: 3 }}>
          <MetricCard title={intl.formatMessage({ id: "subscriptions.singlePurchases" })} value={metricCountValue(metrics?.singlePurchases)} color="info.main" />
        </Grid2>
      </Grid2>

      <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        {intl.formatMessage({ id: "subscriptions.metricsRevenue" })}
      </Typography>
      <Grid2 container spacing={isMobile ? 1 : 2} sx={{ mb: isMobile ? 1.5 : 3 }}>
        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard title={intl.formatMessage({ id: "subscriptions.subscriptionRevenueCollected" })} value={metricCurrencyValue(metrics?.subscriptionRevenueCollected)} />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard title={intl.formatMessage({ id: "subscriptions.singleRevenue" })} value={metricCurrencyValue(metrics?.singleRevenue)} />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard title={intl.formatMessage({ id: "subscriptions.totalRevenue" })} value={metricCurrencyValue(metrics?.totalRevenue)} color="primary.main" />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard title={intl.formatMessage({ id: "subscriptions.subscriptionRevenueTrial" })} value={metricCurrencyValue(metrics?.subscriptionRevenueTrial)} color="text.secondary" />
        </Grid2>
      </Grid2>

      {/* Filters + Table */}
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <PageHeader
          title={intl.formatMessage({ id: "subscriptions.title" })}
          loading={listLoading}
          sx={{ px: 2, py: 1 }}
        />

        <Box sx={{ px: 2, pb: 2 }}>
          <FiltersBar activeFilterCount={activeFilterCount} sticky={false}>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", columnGap: 2, rowGap: 1.5 }}>
              <SearchField
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                placeholder={intl.formatMessage({ id: "subscriptions.searchPlaceholder" })}
                sx={{ width: { xs: "100%", sm: 220 }, marginY: 0 }}
              />

              <Select
                label={intl.formatMessage({ id: "subscriptions.filterType" })}
                value={filters.type}
                onChange={(e) => handleFilterChange("type", e.target.value as string)}
                sx={{ width: { xs: "100%", sm: 140 } }}
              >
                <MenuItem value="">{intl.formatMessage({ id: "common.all" })}</MenuItem>
                <MenuItem value="subscription">{intl.formatMessage({ id: "subscriptionDetail.type.subscription" })}</MenuItem>
                <MenuItem value="single">{intl.formatMessage({ id: "subscriptionDetail.type.singlePurchase" })}</MenuItem>
              </Select>

              <Select
                label={intl.formatMessage({ id: "subscriptions.filterStatus" })}
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value as string)}
                sx={{ width: { xs: "100%", sm: 140 } }}
              >
                <MenuItem value="">{intl.formatMessage({ id: "common.all" })}</MenuItem>
                <MenuItem value="approved">{intl.formatMessage({ id: "subscriptionDetail.status.approved" })}</MenuItem>
                <MenuItem value="pending">{intl.formatMessage({ id: "subscriptionDetail.status.pending" })}</MenuItem>
                <MenuItem value="cancelled">{intl.formatMessage({ id: "subscriptionDetail.status.cancelled" })}</MenuItem>
                <MenuItem value="rejected">{intl.formatMessage({ id: "subscriptionDetail.status.rejected" })}</MenuItem>
                <MenuItem value="superseded">{intl.formatMessage({ id: "subscriptionDetail.status.superseded" })}</MenuItem>
              </Select>

              <Select
                label={intl.formatMessage({ id: "subscriptions.filterOffering" })}
                value={filters.offering}
                onChange={(e) => handleFilterChange("offering", e.target.value as string)}
                sx={{ width: { xs: "100%", sm: 140 } }}
              >
                <MenuItem value="">{intl.formatMessage({ id: "common.all" })}</MenuItem>
                <MenuItem value="premium">Premium</MenuItem>
                <MenuItem value="goalkeeper">Goleiro</MenuItem>
              </Select>

              <Select
                label={intl.formatMessage({ id: "subscriptions.filterBillingPhase" })}
                value={filters.billingPhase}
                onChange={(e) => handleFilterChange("billingPhase", e.target.value as string)}
                sx={{ width: { xs: "100%", sm: 140 } }}
              >
                <MenuItem value="">{intl.formatMessage({ id: "common.all" })}</MenuItem>
                <MenuItem value="trial">{intl.formatMessage({ id: "subscriptions.billingPhase.trial" })}</MenuItem>
                <MenuItem value="active">{intl.formatMessage({ id: "subscriptions.billingPhase.active" })}</MenuItem>
              </Select>

              <FormControlLabel
                sx={{ ml: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={filters.includeTest}
                    onChange={(e) => handleFilterChange("includeTest", e.target.checked)}
                  />
                }
                label={intl.formatMessage({ id: "subscriptions.filterIncludeTest" })}
              />

              <Stack
                direction="row"
                spacing={1}
                alignItems="flex-start"
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                <DatePickerField
                  label={intl.formatMessage({ id: "subscriptions.filterStartDate" })}
                  value={filters.startDate}
                  onChange={(value) => handleFilterChange("startDate", value)}
                  onToday={() => handleFilterChange("startDate", new Date().toISOString().split("T")[0])}
                  onClear={() => handleFilterChange("startDate", "")}
                  minWidth={150}
                  fullWidth={isMobile}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                  size="small"
                />

                <DatePickerField
                  label={intl.formatMessage({ id: "subscriptions.filterEndDate" })}
                  value={filters.endDate}
                  onChange={(value) => handleFilterChange("endDate", value)}
                  onToday={() => handleFilterChange("endDate", new Date().toISOString().split("T")[0])}
                  onClear={() => handleFilterChange("endDate", "")}
                  minWidth={150}
                  fullWidth={isMobile}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                  size="small"
                />
              </Stack>
            </Box>
          </FiltersBar>

          {filters.status && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, pt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {intl.formatMessage({ id: "subscriptions.filterStatus" })}:
              </Typography>
              {statusFilterChip(intl, filters.status, () => handleFilterChange("status", ""))}
            </Box>
          )}

          {!filters.includeTest && hiddenTestCount > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", pt: 1 }}>
              {intl.formatMessage({ id: "subscriptions.hiddenTestCount" }, { count: hiddenTestCount })}
            </Typography>
          )}
        </Box>

        <TableContainer sx={{ maxHeight: isMobile ? "calc(100vh - 320px)" : "calc(100vh - 380px)" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {visibleColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    sx={{
                      minWidth: column.minWidth,
                      whiteSpace: "nowrap",
                      ...(isMobile && column.id === "visitorName" ? { minWidth: 120 } : {}),
                    }}
                    sortDirection={filters.sortBy === column.id ? (filters.sortOrder as "asc" | "desc") : false}
                  >
                    {column.id === "actions" ? null : column.id === "createdAt" || column.id === "amount" || column.id === "status" ? (
                      <Box
                        component="span"
                        onClick={() => handleSortChange(column.id as SortField)}
                        sx={{ cursor: "pointer", userSelect: "none", "&:hover": { opacity: 0.7 } }}
                      >
                        {column.label}
                        {sortIndicator(column.id as SortField)}
                      </Box>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {listLoading && <TableSkeletonRows columns={visibleColumns.length} />}
              {!listLoading && listError && (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} align="center">
                    <Alert severity="error">{listError}</Alert>
                  </TableCell>
                </TableRow>
              )}
              {!listLoading && !listError && rows.map((row) => (
                <TableRow
                  hover
                  key={row.transactionId}
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleOpenDetail(row.transactionId)}
                >
                  {visibleColumns.map((column) => (
                    <TableCell
                      key={column.id}
                      sx={{
                        ...(isMobile && column.id === "visitorName"
                          ? { maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }
                          : {}),
                      }}
                    >
                      {renderCell(column, row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {!listLoading && !listError && rows.length === 0 && (
                <TableEmptyState colSpan={visibleColumns.length}>
                  {intl.formatMessage({ id: "subscriptions.noRecords" })}
                </TableEmptyState>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <StandardTablePagination
          rowsPerPageOptions={isMobile ? [10, 25] : [10, 25, 50, 100]}
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          hideRowsPerPageLabel={isMobile}
        />
      </Paper>

      <SubscriptionDetailDialog
        open={detailOpen}
        setOpen={setDetailOpen}
        transactionId={selectedTransactionId}
      />
    </>
  );
}
